import { priceBySubscription, nameBySubscription } from 'shared-utils/subscription'
import { SubscriptionNameEnum } from 'shared-enums/subscription'

import type { FaqItem } from 'types/section'

type JsonLd = Record<string, unknown>

type BreadcrumbEntry = {
  name: string,
  path: string
}

function getTrimmedBaseUrl (baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '')
}

function toAbsoluteUrl (baseUrl: string, path: string): string {
  const trimmedBase = getTrimmedBaseUrl(baseUrl)
  const trimmedPath = path.startsWith('/') ? path : `/${path}`
  return `${trimmedBase}${trimmedPath}`
}

export function getOrganizationJsonLd (baseUrl: string): JsonLd {
  const base = getTrimmedBaseUrl(baseUrl)
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${base}/#organization`,
    name: companyName,
    legalName: companyName,
    alternateName: appName,
    url: base,
    logo: toAbsoluteUrl(baseUrl, appLogoUrl),
    description: appDescriptionLong,
    foundingDate: companyFoundingDate,
    vatID: companyVatNumber,
    email: companyContactEmail,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Le Tombex 6',
      addressLocality: companyAddressLocality,
      addressRegion: companyAddressRegion,
      postalCode: companyAddressPostalCode,
      addressCountry: companyAddressCountry
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 46.8472831,
      longitude: 6.7028103
    },
    areaServed: [
      { '@type': 'Country', name: 'Suisse' },
      { '@type': 'AdministrativeArea', name: 'Suisse romande' }
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: companyContactEmail,
        telephone: companyContactPhone,
        availableLanguage: ['French']
      }
    ],
    sameAs: [
      'https://www.linkedin.com/company/livedisplay-pro/',
      'https://www.instagram.com/livedisplay_pro/',
      'https://www.facebook.com/livedisplay.pro',
      'https://x.com/livedisplay_pro'
    ]
  }
}

export function getWebSiteJsonLd (baseUrl: string): JsonLd {
  const base = getTrimmedBaseUrl(baseUrl)
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${base}/#website`,
    url: base,
    name: `${appName} — ${appTitle}`,
    description: appDescriptionShort,
    inLanguage: 'fr-CH',
    publisher: { '@id': `${base}/#organization` }
  }
}

export function getSoftwareApplicationJsonLd (baseUrl: string): JsonLd {
  const base = getTrimmedBaseUrl(baseUrl)
  const professionalPrice = `${priceBySubscription[SubscriptionNameEnum.PROFESSIONAL]}`
  const enterprisePrice = `${priceBySubscription[SubscriptionNameEnum.ENTERPRISE]}`

  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: appName,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Digital Signage',
    operatingSystem: 'Web, Windows, Linux',
    description: appDescriptionLong,
    url: base,
    image: toAbsoluteUrl(baseUrl, appLogoUrl),
    inLanguage: 'fr-CH',
    areaServed: { '@type': 'Country', name: 'Suisse' },
    featureList: [
      'Éditeur visuel de diaporamas',
      'Diffusion sur plusieurs écrans',
      'Planification horaire et calendaire',
      'Mise à jour à distance en temps réel',
      'Composition de diaporamas',
      'Travail en équipe multi-utilisateurs'
    ],
    offers: [
      {
        '@type': 'Offer',
        name: nameBySubscription[SubscriptionNameEnum.PROFESSIONAL],
        price: professionalPrice,
        priceCurrency: 'CHF',
        url: toAbsoluteUrl(baseUrl, '/tarifs'),
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: professionalPrice,
          priceCurrency: 'CHF',
          unitText: 'écran/mois',
          referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: 'écran' }
        }
      },
      {
        '@type': 'Offer',
        name: nameBySubscription[SubscriptionNameEnum.ENTERPRISE],
        price: enterprisePrice,
        priceCurrency: 'CHF',
        url: toAbsoluteUrl(baseUrl, '/tarifs'),
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: enterprisePrice,
          priceCurrency: 'CHF',
          unitText: 'écran/mois',
          referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: 'écran' }
        }
      }
    ],
    publisher: { '@id': `${base}/#organization` }
  }
}

export function getBoxProductJsonLd (baseUrl: string): JsonLd {
  const base = getTrimmedBaseUrl(baseUrl)
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `Box ${appName}`,
    description: 'Box HDMI 4K d\'affichage dynamique assemblée en Suisse, prête à brancher sur tout écran. Mise à jour et actions à distance, état suivi en continu, fonctionnement 24h/24, mode veille intelligent.',
    image: toAbsoluteUrl(baseUrl, '/images/box.png'),
    brand: { '@type': 'Brand', name: appName },
    manufacturer: { '@id': `${base}/#organization` },
    countryOfOrigin: { '@type': 'Country', name: 'Suisse' },
    category: 'Lecteur d\'affichage dynamique',
    offers: {
      '@type': 'Offer',
      url: toAbsoluteUrl(baseUrl, '/box'),
      price: '199',
      priceCurrency: 'CHF',
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      priceValidUntil: `${new Date().getUTCFullYear() + 1}-12-31`,
      seller: { '@id': `${base}/#organization` }
    }
  }
}

export function getLocalBusinessJsonLd (baseUrl: string): JsonLd {
  const base = getTrimmedBaseUrl(baseUrl)
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${base}/#localbusiness`,
    name: companyName,
    alternateName: appName,
    url: base,
    logo: toAbsoluteUrl(baseUrl, appLogoUrl),
    image: toAbsoluteUrl(baseUrl, appLogoUrl),
    description: 'Fourniture, pose et installation d\'écrans professionnels, murs d\'écrans LED, bornes tactiles et totems vidéo en Suisse, gérés à distance depuis le logiciel d\'affichage dynamique Live Display.',
    email: companyContactEmail,
    telephone: companyContactPhone,
    vatID: companyVatNumber,
    priceRange: 'CHF',
    parentOrganization: { '@id': `${base}/#organization` },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '18:00'
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Le Tombex 6',
      addressLocality: companyAddressLocality,
      addressRegion: companyAddressRegion,
      postalCode: companyAddressPostalCode,
      addressCountry: companyAddressCountry
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 46.8472831,
      longitude: 6.7028103
    },
    areaServed: [
      { '@type': 'Country', name: 'Suisse' },
      { '@type': 'AdministrativeArea', name: 'Suisse romande' }
    ]
  }
}

// Pricing is on-quote, so screen types are modeled as an OfferCatalog of services (no fabricated Product prices).
export function getScreensServiceJsonLd (baseUrl: string, screenTypes: string[]): JsonLd {
  const base = getTrimmedBaseUrl(baseUrl)
  const toOffer = (name: string): JsonLd => ({
    '@type': 'Offer',
    itemOffered: { '@type': 'Service', name }
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Écrans professionnels d\'affichage dynamique',
    name: `Écrans professionnels — ${appName}`,
    description: 'Fourniture, pose et installation d\'écrans professionnels, murs d\'écrans LED, bornes tactiles et totems vidéo en Suisse, gérés à distance depuis le logiciel Live Display.',
    url: toAbsoluteUrl(baseUrl, '/ecrans'),
    provider: { '@id': `${base}/#organization` },
    areaServed: [
      { '@type': 'Country', name: 'Suisse' },
      { '@type': 'AdministrativeArea', name: 'Suisse romande' }
    ],
    hasOfferCatalog: [
      {
        '@type': 'OfferCatalog',
        name: 'Écrans professionnels',
        itemListElement: screenTypes.map(toOffer)
      }
    ]
  }
}

export function getContactPageJsonLd (baseUrl: string): JsonLd {
  const base = getTrimmedBaseUrl(baseUrl)
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    '@id': `${base}/contact/#contactpage`,
    url: toAbsoluteUrl(baseUrl, '/contact'),
    name: `Contact & support client — ${appName}`,
    inLanguage: 'fr-CH',
    about: { '@id': `${base}/#organization` },
    mainEntity: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: companyContactEmail,
      telephone: companyContactPhone,
      availableLanguage: ['French']
    }
  }
}

export function getFaqPageJsonLd (items: FaqItem[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  }
}

export function getBreadcrumbJsonLd (baseUrl: string, entries: BreadcrumbEntry[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: entries.map((entry, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: entry.name,
      item: toAbsoluteUrl(baseUrl, entry.path)
    }))
  }
}
